import {
  ChatViewWrapper,
  IconWrapper,
  PreviewTitle,
  PreviewInput,
  PreviewInputWrapper,
  PreviewDisclaimer,
  PreviewContentWrapper,
  TrialExpiredWrapper,
  TrialExpiredContent,
  TrialExpiredTitle,
  TrialExpiredSubtitle,
  TrialExpiredCard,
  TrialExpiredCardIcon,
  TrialExpiredCardTitle,
  TrialExpiredCardDescription,
  TrialExpiredButton,
  TrialExpiredButtonText,
  TrialExpiredFeatures,
  TrialExpiredFeatureCard,
  TrialExpiredFeatureIcon,
  TrialExpiredFeatureTitle,
  TrialExpiredFeatureDescription,
  TransitionOverlay,
  TransitionBubble,
  SuggestionChipsWrapper,
  SuggestionChip,
  SuggestionChipIcon,
} from "./Styles";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { FiSend, FiPaperclip } from "react-icons/fi";
import { Button } from "../../Components/Button";
import { Chat } from "../../Components/Chat";
import { Chat as ChatDTO, Language } from "../../Common/Types-DTOs/Chat";
import {
  MessageDTOType,
  MessageSenderType as Sender,
} from "../../Common/Types-DTOs/Message";
import { useChat } from "../../Common/Hooks/useChat";
import { useBusiness } from "../../Common/Hooks/useBusiness";
import { useApi } from "../../Common/Hooks/useApi";
import { usePushNotifications } from "../../Common/Hooks/usePushNotifications";
import { usePlatformConfig } from "../../Common/Hooks/usePlatformConfig";
import { isErr } from "../../Common/Utils/Result";
import { getErrorMessage } from "../../Common/Utils/errorMessages";
import { isSubscriptionActive, isSubscriptionIncomplete as checkSubscriptionIncomplete } from "../../Common/Utils/subscriptionUtils";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../../Components/Notifications";
import { PushOptIn } from "../../Components/PushOptIn";
import { ChatCreditModal, isChatCreditError } from "../../Components/ChatCreditModal";
import MikeLogoSmall from "../../Assets/Mike_logo_1.png";
import { openSseStream } from "../../Common/Utils/openSseStream";
import { dueDiligenceChatActionAtom } from "../../Common/State/dueDiligenceChatAction";
import { authState } from "../../Common/State/Auth";
import { combineAttachedFiles } from "../../Common/Utils/documentAttachments";
import { caseAliasFromAction } from "../../Common/Utils/caseAliasFromAction";

export const ChatView = () => {
  const { t } = useTranslation();
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [chat, setChat] = useState<ChatDTO | undefined>(undefined);
  const [pendingDocuments, setPendingDocuments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [thinkingText, setThinkingText] = useState<string | null>(null);
  const [streamingBotMessageId, setStreamingBotMessageId] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [creditModalMessage, setCreditModalMessage] = useState<string | null>(null);
  const [, setDueDiligenceChatAction] = useRecoilState(dueDiligenceChatActionAtom);
  const [auth] = useRecoilState(authState);
  const { createChat, getChatById, sendMessage, sendDocument, setMessageFeedback } = useChat();
  const { business } = useBusiness();
  const { getChatCreditsEndpoint } = useApi();
  const platformConfig = usePlatformConfig();
  const { isSubscribed: isPushSubscribed, shouldShowOptIn, subscribe: pushSubscribe, dismissOptIn } = usePushNotifications();
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(false);
  const [isTrialExpired, setIsTrialExpired] = useState<boolean>(false);
  const [chatCredits, setChatCredits] = useState<{
    unlimited: boolean;
    availableChatCredits: number;
  } | null>(null);
  const [isSubscriptionIncomplete, setIsSubscriptionIncomplete] = useState<boolean>(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState<boolean>(true);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeStreamRef = useRef<{ close: () => void } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewFileInputRef = useRef<HTMLInputElement | null>(null);
  const processedActionRef = useRef<string | null>(null);

  // Polling interval constants
  const SHORT_INTERVAL = 500; // 500ms for immediate response detection
  const LONG_INTERVAL = 30000; // 30 seconds for ongoing polling

  // Push mode safety-net constants
  const RETRY_INTERVAL = 60000; // 60 seconds
  const MAX_RETRIES = 3;

  const startSafetyNetRetry = (chat: ChatDTO) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const targetChatId = chat.id;
    const initialMessageLength = chat.messages.length;
    let retryCount = 0;

    const retryId = setInterval(async () => {
      retryCount++;
      const response = await handleGetChat(targetChatId);
      if (response) {
        const hasNewMessages = response.chat.messages.length > initialMessageLength;
        if (hasNewMessages) {
          setChat(response.chat);
          clearInterval(retryId);
          if (intervalRef.current === retryId) {
            intervalRef.current = null;
          }
          return;
        }
      }
      if (retryCount >= MAX_RETRIES) {
        clearInterval(retryId);
        if (intervalRef.current === retryId) {
          intervalRef.current = null;
        }
      }
    }, RETRY_INTERVAL);
    intervalRef.current = retryId;
  };

  const afterMessageSent = (chat: ChatDTO, userMessageId?: string) => {
    // Close any prior stream or polling interval (in case of rapid successive messages)
    if (activeStreamRef.current) {
      activeStreamRef.current.close();
      activeStreamRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!platformConfig.chatSseEnabled || !userMessageId) {
      if (isPushSubscribed) startSafetyNetRetry(chat);
      else polling(chat);
      return;
    }

    activeStreamRef.current = openSseStream(chat.id, userMessageId, {
      onThought: (text) => {
        setThinkingText(text);
      },
      onDelta: (delta) => {
        setStreamingText((prev) => (prev ?? "") + delta);
      },
      onDone: (
        botMessageId,
        fullText,
        creationDate,
        action,
        documentUrl,
        documentName
      ) => {
        setThinkingText(null);
        setStreamingText(null);
        setStreamingBotMessageId(null);
        setChat((prev) =>
          prev
            ? {
                ...prev,
                messages: [
                  ...prev.messages,
                  {
                    id: botMessageId,
                    messageType: MessageDTOType.TEXT,
                    text: fullText,
                    sender: Sender.BOT,
                    creationDate,
                  },
                  ...(documentUrl
                    ? [
                        {
                          id: `${botMessageId}-document`,
                          messageType: MessageDTOType.DOCUMENT,
                          documentUrl,
                          documentName: documentName ?? "",
                          sender: Sender.BOT,
                          creationDate,
                        },
                      ]
                    : []),
                ],
              }
            : prev
        );
        if (activeStreamRef.current) {
          activeStreamRef.current.close();
          activeStreamRef.current = null;
        }

        if (
          action?.type === "OPEN_DD_MODAL" &&
          processedActionRef.current !== botMessageId
        ) {
          processedActionRef.current = botMessageId;
          const actionData = (action.data ?? {}) as Record<string, unknown>;
          setIsTransitioning(true);
          setTimeout(() => {
            setDueDiligenceChatAction({
              chatId: chat.id,
              data: {
                name: actionData["name"] as string | undefined,
                address: actionData["address"] as string | undefined,
                cadastralReference: actionData["cadastralReference"] as string | undefined,
                propertyType: actionData["propertyType"] as string | undefined,
              },
              miniWidgetVisible: true,
            });
            navigate("/due-diligence");
          }, 700);
        } else if (
          action?.type === "NAVIGATE_TO_DD_CASE" &&
          processedActionRef.current !== botMessageId
        ) {
          processedActionRef.current = botMessageId;
          const caseAlias = caseAliasFromAction(action.data);
          if (caseAlias) {
            setIsTransitioning(true);
            setTimeout(() => {
              setDueDiligenceChatAction({ chatId: chat.id, data: {}, miniWidgetVisible: true });
              navigate(`/due-diligence/case/${caseAlias}/report`);
            }, 700);
          }
        } else if (
          action?.type === "NAVIGATE_TO_DD_LIST" &&
          processedActionRef.current !== botMessageId
        ) {
          processedActionRef.current = botMessageId;
          setIsTransitioning(true);
          setTimeout(() => {
            setDueDiligenceChatAction({ chatId: chat.id, data: {}, miniWidgetVisible: true });
            navigate("/due-diligence");
          }, 700);
        } else if (
          action?.type === "NAVIGATE_TO_CONTRACT" &&
          processedActionRef.current !== botMessageId
        ) {
          processedActionRef.current = botMessageId;
          const contractId = (action.data ?? {})["contractId"] as string | undefined;
          if (contractId) {
            setIsTransitioning(true);
            setTimeout(() => {
              setDueDiligenceChatAction({ chatId: chat.id, data: {}, miniWidgetVisible: true });
              navigate(`/contracts/${contractId}`);
            }, 700);
          }
        } else if (
          action?.type === "NAVIGATE_TO_CONTRACT_LIST" &&
          processedActionRef.current !== botMessageId
        ) {
          processedActionRef.current = botMessageId;
          setIsTransitioning(true);
          setTimeout(() => {
            setDueDiligenceChatAction({ chatId: chat.id, data: {}, miniWidgetVisible: true });
            navigate("/contracts");
          }, 700);
        }
      },
      onError: () => {
        setStreamingText(null);
        setThinkingText(null);
        setStreamingBotMessageId(null);
        if (activeStreamRef.current) {
          activeStreamRef.current.close();
        }
        activeStreamRef.current = null;
        polling(chat);
      },
    }, auth.token);
  };

  const polling = async (chat: ChatDTO) => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const chatId = chat.id;
    const initialMessageLength = chat.messages.length;
    let isBotResponding = false;

    const intervalId = setInterval(async () => {
      // Skip if an SSE stream is active — it owns the state, polling would clobber it.
      if (activeStreamRef.current) return;

      const response = await handleGetChat(chatId);

      if (!response) return;

      const currentMessageLength = response.chat.messages.length;

      if (currentMessageLength !== initialMessageLength) {
        // Messages have changed, update the chat
        setChat(response.chat);

        // Check if the bot has started responding
        const lastMessage = response.chat.messages[currentMessageLength - 1];
        const isLastMessageFromBot = lastMessage && lastMessage.sender === Sender.BOT;

        if (isLastMessageFromBot && !isBotResponding) {
          // Detect DD action on new bot message
          if (
            lastMessage.action?.type === "OPEN_DD_MODAL" &&
            processedActionRef.current !== lastMessage.id
          ) {
            processedActionRef.current = lastMessage.id;
            const actionData = lastMessage.action.data ?? {};
            setIsTransitioning(true);
            clearInterval(intervalId);
            intervalRef.current = null;
            setTimeout(() => {
              setDueDiligenceChatAction({
                chatId: chatId,
                data: {
                  name: actionData["name"] as string | undefined,
                  address: actionData["address"] as string | undefined,
                  cadastralReference: actionData["cadastralReference"] as string | undefined,
                  propertyType: actionData["propertyType"] as string | undefined,
                },
                miniWidgetVisible: true,
              });
              navigate("/due-diligence");
            }, 700);
            return;
          }
          if (
            lastMessage.action?.type === "NAVIGATE_TO_DD_CASE" &&
            processedActionRef.current !== lastMessage.id
          ) {
            processedActionRef.current = lastMessage.id;
            const caseAlias = caseAliasFromAction(lastMessage.action.data);
            if (caseAlias) {
              setIsTransitioning(true);
              clearInterval(intervalId);
              intervalRef.current = null;
              setTimeout(() => {
                setDueDiligenceChatAction({ chatId, data: {}, miniWidgetVisible: true });
                navigate(`/due-diligence/case/${caseAlias}/report`);
              }, 700);
              return;
            }
          }
          if (
            lastMessage.action?.type === "NAVIGATE_TO_DD_LIST" &&
            processedActionRef.current !== lastMessage.id
          ) {
            processedActionRef.current = lastMessage.id;
            setIsTransitioning(true);
            clearInterval(intervalId);
            intervalRef.current = null;
            setTimeout(() => {
              setDueDiligenceChatAction({ chatId, data: {}, miniWidgetVisible: true });
              navigate("/due-diligence");
            }, 700);
            return;
          }
          if (
            lastMessage.action?.type === "NAVIGATE_TO_CONTRACT" &&
            processedActionRef.current !== lastMessage.id
          ) {
            processedActionRef.current = lastMessage.id;
            const contractId = lastMessage.action.data?.["contractId"] as string | undefined;
            if (contractId) {
              setIsTransitioning(true);
              clearInterval(intervalId);
              intervalRef.current = null;
              setTimeout(() => {
                setDueDiligenceChatAction({ chatId, data: {}, miniWidgetVisible: true });
                navigate(`/contracts/${contractId}`);
              }, 700);
              return;
            }
          }
          if (
            lastMessage.action?.type === "NAVIGATE_TO_CONTRACT_LIST" &&
            processedActionRef.current !== lastMessage.id
          ) {
            processedActionRef.current = lastMessage.id;
            setIsTransitioning(true);
            clearInterval(intervalId);
            intervalRef.current = null;
            setTimeout(() => {
              setDueDiligenceChatAction({ chatId, data: {}, miniWidgetVisible: true });
              navigate("/contracts");
            }, 700);
            return;
          }

          // Bot has started responding, switch to slow polling
          isBotResponding = true;
          clearInterval(intervalId);

          // Start slow polling (30 seconds)
          const slowIntervalId = setInterval(async () => {
            const slowResponse = await handleGetChat(chatId);
            if (slowResponse) {
              setChat(slowResponse.chat);
            }
          }, LONG_INTERVAL);

          intervalRef.current = slowIntervalId;
        }
      }
    }, SHORT_INTERVAL); // Fast polling (500ms) initially
    intervalRef.current = intervalId;
  };

  // Paints a bot bubble in local state only (the backend has not stored anything)
  const appendLocalBotMessage = (text: string) => {
    setChat((prev) =>
      prev
        ? {
            ...prev,
            messages: [
              ...prev.messages,
              {
                id: `local-${Date.now()}`,
                messageType: MessageDTOType.TEXT,
                text,
                sender: Sender.BOT,
                creationDate: new Date(),
              },
            ],
          }
        : prev
    );
  };

  const handleCreditModalDismiss = () => {
    if (creditModalMessage) {
      appendLocalBotMessage(creditModalMessage);
    }
    setCreditModalMessage(null);
  };

  const handleSendMessage = async (message: string) => {
    if (!chat) return;
    setStreamingText(null);
    setStreamingBotMessageId(null);

    const updatedChat = {
      ...chat,
      messages: [
        ...chat.messages,
        {
          id: "",
          messageType: MessageDTOType.TEXT,
          text: message,
          sender: Sender.USER,
          creationDate: new Date(),
        }
      ]
    };

    setChat(updatedChat);

    const response = await sendMessage(chat.id, message);

    if (isErr(response) && isChatCreditError(response.error)) {
      setCreditModalMessage(response.error.message);
      return;
    }

    afterMessageSent(updatedChat, isErr(response) ? undefined : response.value.userMessageId);
  };

  const handleMessageFeedback = async (messageId: string, feedback: 1 | -1 | 0) => {
    if (!chat) return;
    await setMessageFeedback(chat.id, messageId, feedback);
  };

  const handleSendDocument = async (files: File[], text?: string) => {
    if (!chat) return;
    setStreamingText(null);
    setStreamingBotMessageId(null);
    const prevChat = chat;

    const optimisticMessages: ChatDTO["messages"] = [
      ...chat.messages,
      ...files.map((file) => ({
        id: "",
        messageType: MessageDTOType.DOCUMENT,
        sender: Sender.USER,
        creationDate: new Date(),
        documentName: file.name,
        documentUrl: "",
      })),
      ...(text ? [{
        id: "",
        messageType: MessageDTOType.TEXT,
        text,
        sender: Sender.USER,
        creationDate: new Date(),
      }] : []),
    ];
    setChat({ ...chat, messages: optimisticMessages });

    const response = await sendDocument(chat.id, files, text);
    if (isErr(response)) {
      eventBus.emit("NOTIFICATION", Notification.buildNotification("error", getErrorMessage(response.error.errorCode)));
      setChat(prevChat);
      return;
    }

    afterMessageSent(response.value.chat, response.value.userMessageId);
  };

  const handlePreviewAttachClick = useCallback(() => {
    previewFileInputRef.current?.click();
  }, []);

  const handlePreviewFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);

    // Reset file input
    if (previewFileInputRef.current) {
      previewFileInputRef.current.value = "";
    }
    if (files.length === 0) return;

    setPendingDocuments((prev) => combineAttachedFiles(files, prev));
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const handleGetChat = async (chatId: string) => {
    const response = await getChatById(chatId);

    if (isErr(response)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("chat.chatView.errors.getChatFailed"))
      );
      return;
    }

    return response.value;
  };

  const handleCreateChat = async () => {
    if (!inputValue && pendingDocuments.length === 0) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const message = inputValue;
    const docFiles = pendingDocuments;
    setInputValue("");
    setPendingDocuments([]);

    const optimisticMessages: ChatDTO["messages"] = [];
    for (const docFile of docFiles) {
      optimisticMessages.push({
        id: "",
        messageType: MessageDTOType.DOCUMENT,
        sender: Sender.USER,
        creationDate: new Date(),
        documentName: docFile.name,
        documentUrl: "",
      });
    }
    if (message) {
      optimisticMessages.push({
        id: "",
        messageType: MessageDTOType.TEXT,
        text: message,
        sender: Sender.USER,
        creationDate: new Date(),
      });
    }

    setChat({
      id: "",
      contact: "",
      language: Language.SPANISH,
      messages: optimisticMessages,
      creationDate: new Date(),
    });

    let response;
    if (docFiles.length > 0) {
      response = await sendDocument(undefined, docFiles, message || undefined);
    } else {
      response = await createChat(message);
    }

    if (isErr(response)) {
      if (isChatCreditError(response.error)) {
        // Keep the optimistic chat so the message can be painted as a bot bubble on dismiss
        setCreditModalMessage(response.error.message);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      eventBus.emit("NOTIFICATION", Notification.buildNotification("error", getErrorMessage(response.error.errorCode)));
      setChat(undefined);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    const newChat = response.value.chat;
    setChat(newChat);
    afterMessageSent(newChat, response.value.userMessageId);
    navigate(`/chat/${newChat.id}`, { replace: true });
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (chatId) {
      handleGetChat(chatId).then((response) => {
        if (response) {
          setChat(response.chat);
          if (!activeStreamRef.current) {
            // Polling is safe even in SSE mode: the setInterval guard skips iterations
            // while an SSE stream is active (activeStreamRef.current), so it won't
            // interfere with streaming or clobber local state.
            if (!isPushSubscribed) {
              const lastMsg = response.chat.messages[response.chat.messages.length - 1];
              if (!platformConfig.chatSseEnabled || lastMsg?.sender === Sender.USER) {
                polling(response.chat);
              }
            }
          }
        }
      });
    } else {
      setChat(undefined);
      setInputValue("");
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setStreamingText(null);
      setStreamingBotMessageId(null);
      setThinkingText(null);
      // Skip when chatId is undefined: handleCreateChat's navigate() fires this cleanup
      // before the stream opens, aborting it immediately.
      if (chatId && activeStreamRef.current) {
        activeStreamRef.current.close();
        activeStreamRef.current = null;
      }
    };
  }, [chatId]);

  // Listen for push-driven chat refreshes
  useEffect(() => {
    const handlePushMessage = (data: any) => {
      if (!data?.chatId || !chatId) return;

      if (data.chatId === chatId) {
        // Silent refresh — fetch latest messages
        handleGetChat(chatId).then((response) => {
          if (response) {
            setChat(response.chat);
            // Cancel safety-net retry if active
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        });
      }
    };

    eventBus.on("PUSH_NEW_MESSAGE", handlePushMessage);

    return () => {
      eventBus.off("PUSH_NEW_MESSAGE", handlePushMessage);
    };
  }, [chatId]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  // Check subscription and trial status
  useEffect(() => {
    const checkSubscriptionAndTrial = async () => {
      if (!business) {
        return;
      }

      // Staff-owned businesses always have chat access
      if (business.isStaffOwned) {
        setHasActiveSubscription(true);
        setIsTrialExpired(false);
        setIsSubscriptionIncomplete(false);
        setIsCheckingSubscription(false);
        return;
      }

      setIsCheckingSubscription(true);

      // Kept by GET /business, which reads it from our own table, not Stripe
      const subscription = business.copilotSubscription;
      if (checkSubscriptionIncomplete(subscription)) {
        setHasActiveSubscription(false);
        setIsSubscriptionIncomplete(true);
        setIsCheckingSubscription(false);
        return;
      }
      setIsSubscriptionIncomplete(false);

      if (isSubscriptionActive(subscription)) {
        setHasActiveSubscription(true);
        setIsTrialExpired(false);
        setIsCheckingSubscription(false);
        return;
      }

      // If no active subscription, check if trial has expired
      setHasActiveSubscription(false);
      setIsSubscriptionIncomplete(false);

      // Freemium: chat stays open while there are credits left
      try {
        const creditsResponse = await getChatCreditsEndpoint();
        if (!isErr(creditsResponse) && creditsResponse.value) {
          setChatCredits({
            unlimited: creditsResponse.value.unlimited,
            availableChatCredits: creditsResponse.value.availableChatCredits,
          });
        }
      } catch (error) {
        // If credits cannot be fetched, fall back to the trial/subscription logic
      }

      if (business.trialEndDate) {
        const trialEndDate = new Date(business.trialEndDate);
        const now = new Date();
        if (trialEndDate <= now) {
          setIsTrialExpired(true);
        } else {
          setIsTrialExpired(false);
        }
      } else {
        // If no trial, consider as expired to show the block
        setIsTrialExpired(true);
      }

      setIsCheckingSubscription(false);
    };

    checkSubscriptionAndTrial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id, business?.trialEndDate, business?.copilotSubscription?.status]);

  const isChatBlocked = useMemo(() => {
    // Staff-owned businesses are never blocked
    if (business?.isStaffOwned) {
      return false;
    }

    // If subscription is incomplete, block
    if (isSubscriptionIncomplete) {
      return true;
    }

    // Block chat if:
    // 1. No active subscription AND trial has expired, OR
    // 2. No active subscription AND no chat credits left
    if (hasActiveSubscription) {
      return false;
    }

    // Freemium with credits (or unlimited flag from the backend) can chat
    if (chatCredits && (chatCredits.unlimited || chatCredits.availableChatCredits > 0)) {
      return false;
    }

    // If no active subscription, check trial status
    if (business?.trialEndDate) {
      const trialEndDate = new Date(business.trialEndDate);
      const now = new Date();
      // If trial has expired, block
      return trialEndDate <= now;
    }

    // No subscription, no credits and no trial: block
    return true;
  }, [hasActiveSubscription, business?.trialEndDate, isSubscriptionIncomplete, chatCredits]);

  const isOutOfCredits = useMemo(
    () => !!chatCredits && !chatCredits.unlimited && chatCredits.availableChatCredits <= 0,
    [chatCredits]
  );

  const SUGGESTIONS = [
    {
      label: t("chat.chatView.suggestions.legalReview.label"),
      description: t("chat.chatView.suggestions.legalReview.description"),
      prompt: t("chat.chatView.suggestions.legalReview.prompt"),
      icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>,
      bg: '#f3e8ff', fg: '#9333ea',
    },
    {
      label: t("chat.chatView.suggestions.documentSummary.label"),
      description: t("chat.chatView.suggestions.documentSummary.description"),
      prompt: t("chat.chatView.suggestions.documentSummary.prompt"),
      icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>,
      bg: '#dbeafe', fg: '#2563eb',
    },
    {
      label: t("chat.chatView.suggestions.precedentSearch.label"),
      description: t("chat.chatView.suggestions.precedentSearch.description"),
      prompt: t("chat.chatView.suggestions.precedentSearch.prompt"),
      icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>,
      bg: '#fef3c7', fg: '#d97706',
    },
    {
      label: t("chat.chatView.suggestions.clauseDrafting.label"),
      description: t("chat.chatView.suggestions.clauseDrafting.description"),
      prompt: t("chat.chatView.suggestions.clauseDrafting.prompt"),
      icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>,
      bg: '#d1fae5', fg: '#059669',
    },
    {
      label: t("chat.chatView.suggestions.dueDiligence.label"),
      description: t("chat.chatView.suggestions.dueDiligence.description"),
      prompt: t("chat.chatView.suggestions.dueDiligence.prompt"),
      icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/></svg>,
      bg: '#e0f2fe', fg: '#0284c7',
    },
  ]

  const getFileTypeInfo = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return { label: 'PDF', bg: '#fee2e2', fg: '#dc2626' };
    if (ext === 'docx' || ext === 'doc') return { label: 'DOC', bg: '#dbeafe', fg: '#2563eb' };
    return { label: 'TXT', bg: '#f3f4f6', fg: '#6b7280' };
  };

  const handleSuggestionClick = (prompt: string) => {
    setInputValue(prompt)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  const handleUpgradeClick = () => {
    navigate('/choose-plan');
  };

  const handleCompletePaymentClick = () => {
    eventBus.emit("OPEN_SUBSCRIPTION_MODAL");
  };

  if (isCheckingSubscription) {
    return (
      <ChatViewWrapper>
        <PreviewContentWrapper>
          <PreviewTitle>{t("chat.chatView.loadingTitle")}</PreviewTitle>
        </PreviewContentWrapper>
      </ChatViewWrapper>
    );
  }

  if (isChatBlocked) {
    // Specific rendering for incomplete subscription
    if (isSubscriptionIncomplete) {
      return (
        <ChatViewWrapper>
          <TrialExpiredWrapper>
            <TrialExpiredContent>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', textAlign: 'center', marginBottom: '2.5rem' }}>
                <TrialExpiredTitle>{t("chat.chatView.trialExpired.incompleteSubscription.title")}</TrialExpiredTitle>
                <TrialExpiredSubtitle>{t("chat.chatView.trialExpired.incompleteSubscription.subtitle")}</TrialExpiredSubtitle>
              </div>

              <TrialExpiredCard>
                <TrialExpiredCardIcon>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="#EAB308" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                </TrialExpiredCardIcon>
                <TrialExpiredCardTitle>{t("chat.chatView.trialExpired.incompleteSubscription.cardTitle")}</TrialExpiredCardTitle>
                <TrialExpiredCardDescription>
                  {t("chat.chatView.trialExpired.incompleteSubscription.cardDescription")}
                </TrialExpiredCardDescription>
                <TrialExpiredButton onClick={handleCompletePaymentClick}>
                  <TrialExpiredButtonText>{t("chat.chatView.trialExpired.incompleteSubscription.completePaymentButton")}</TrialExpiredButtonText>
                </TrialExpiredButton>
              </TrialExpiredCard>
            </TrialExpiredContent>
          </TrialExpiredWrapper>
        </ChatViewWrapper>
      );
    }

    // Rendering for expired trial or no subscription (existing)
    return (
      <ChatViewWrapper>
        <TrialExpiredWrapper>
          <TrialExpiredContent>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', textAlign: 'center', marginBottom: '2.5rem' }}>
              <TrialExpiredTitle>
                {business?.trialEndDate && isTrialExpired
                  ? t("chat.chatView.trialExpired.title.trialEnded")
                  : isOutOfCredits
                    ? t("chat.chatView.trialExpired.title.outOfCredits")
                    : t("chat.chatView.trialExpired.title.default")}
              </TrialExpiredTitle>
              <TrialExpiredSubtitle>
                {isOutOfCredits
                  ? t("chat.chatView.trialExpired.subtitle.outOfCredits")
                  : t("chat.chatView.trialExpired.subtitle.default")}
              </TrialExpiredSubtitle>
            </div>

            <TrialExpiredCard>
              <TrialExpiredCardIcon>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                </svg>
              </TrialExpiredCardIcon>
              <TrialExpiredCardTitle>{t("chat.chatView.trialExpired.cardTitle")}</TrialExpiredCardTitle>
              <TrialExpiredCardDescription>
                {business?.trialEndDate && isTrialExpired
                  ? t("chat.chatView.trialExpired.cardDescription.trialEnded")
                  : isOutOfCredits
                    ? t("chat.chatView.trialExpired.cardDescription.outOfCredits")
                    : t("chat.chatView.trialExpired.cardDescription.default")}
              </TrialExpiredCardDescription>
              {isOutOfCredits && (
                <TrialExpiredButton
                  onClick={() => navigate('/buy-credits')}
                  style={{ width: '100%', maxWidth: '20rem' }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '0.5rem' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93V18h-2v1.93c-3.61-.45-6.48-3.32-6.93-6.93H6v-2H4.07c.45-3.61 3.32-6.48 6.93-6.93V6h2V4.07c3.61.45 6.48 3.32 6.93 6.93H18v2h1.93c-.45 3.61-3.32 6.48-6.93 6.93z" />
                  </svg>
                  <TrialExpiredButtonText>{t("chat.chatView.trialExpired.buyCreditsButton")}</TrialExpiredButtonText>
                </TrialExpiredButton>
              )}
              <TrialExpiredButton
                onClick={handleUpgradeClick}
                style={
                  isOutOfCredits
                    ? { width: '100%', maxWidth: '20rem', backgroundColor: '#ffffff', color: '#0b1b2b', border: '1px solid #0b1b2b', boxShadow: 'none' }
                    : { width: '100%', maxWidth: '20rem' }
                }
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '0.5rem' }}>
                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                </svg>
                <TrialExpiredButtonText>{t("chat.chatView.trialExpired.upgradeButton")}</TrialExpiredButtonText>
              </TrialExpiredButton>
            </TrialExpiredCard>

            <TrialExpiredFeatures>
              <TrialExpiredFeatureCard>
                <TrialExpiredFeatureIcon style={{ backgroundColor: '#f3e8ff', color: '#9333ea' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor" />
                  </svg>
                </TrialExpiredFeatureIcon>
                <div>
                  <TrialExpiredFeatureTitle>{t("chat.chatView.trialExpired.features.legalReview.title")}</TrialExpiredFeatureTitle>
                  <TrialExpiredFeatureDescription>{t("chat.chatView.trialExpired.features.legalReview.description")}</TrialExpiredFeatureDescription>
                </div>
              </TrialExpiredFeatureCard>

              <TrialExpiredFeatureCard>
                <TrialExpiredFeatureIcon style={{ backgroundColor: '#dbeafe', color: '#2563eb' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor" />
                  </svg>
                </TrialExpiredFeatureIcon>
                <div>
                  <TrialExpiredFeatureTitle>{t("chat.chatView.trialExpired.features.documentSummary.title")}</TrialExpiredFeatureTitle>
                  <TrialExpiredFeatureDescription>{t("chat.chatView.trialExpired.features.documentSummary.description")}</TrialExpiredFeatureDescription>
                </div>
              </TrialExpiredFeatureCard>

              <TrialExpiredFeatureCard>
                <TrialExpiredFeatureIcon style={{ backgroundColor: '#fef3c7', color: '#d97706' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor" />
                  </svg>
                </TrialExpiredFeatureIcon>
                <div>
                  <TrialExpiredFeatureTitle>{t("chat.chatView.trialExpired.features.precedentSearch.title")}</TrialExpiredFeatureTitle>
                  <TrialExpiredFeatureDescription>{t("chat.chatView.trialExpired.features.precedentSearch.description")}</TrialExpiredFeatureDescription>
                </div>
              </TrialExpiredFeatureCard>

              <TrialExpiredFeatureCard>
                <TrialExpiredFeatureIcon style={{ backgroundColor: '#d1fae5', color: '#059669' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor" />
                  </svg>
                </TrialExpiredFeatureIcon>
                <div>
                  <TrialExpiredFeatureTitle>{t("chat.chatView.trialExpired.features.clauseDrafting.title")}</TrialExpiredFeatureTitle>
                  <TrialExpiredFeatureDescription>{t("chat.chatView.trialExpired.features.clauseDrafting.description")}</TrialExpiredFeatureDescription>
                </div>
              </TrialExpiredFeatureCard>
            </TrialExpiredFeatures>
          </TrialExpiredContent>
        </TrialExpiredWrapper>
      </ChatViewWrapper>
    );
  }

  return (
    <>
      <ChatCreditModal
        message={creditModalMessage}
        onDismiss={handleCreditModalDismiss}
        onBuy={() => setCreditModalMessage(null)}
      />
      {isTransitioning && (
        <>
          <TransitionOverlay />
          <TransitionBubble>
            <img src={MikeLogoSmall} alt={t("chat.chatView.preview.mikeLogoAlt")} />
          </TransitionBubble>
        </>
      )}
    <ChatViewWrapper style={chat ? { alignItems: 'flex-start', justifyContent: 'flex-start' } : {}}>
      {!chat ? (
        <PreviewContentWrapper>
          <IconWrapper>
            <img
              src="/mike_logo.png"
              alt={t("chat.chatView.preview.mikeLogoAlt")}
              style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
            />
          </IconWrapper>
          <PreviewTitle>{t("chat.chatView.preview.title")}</PreviewTitle>
          {shouldShowOptIn && (
            <PushOptIn onSubscribe={pushSubscribe} onDismiss={dismissOptIn} />
          )}
          <PreviewInputWrapper>
            <input
              type="file"
              multiple
              ref={previewFileInputRef}
              onChange={handlePreviewFileChange}
              accept="application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
            />
            <Button
              onClick={handlePreviewAttachClick}
              title={t("chat.chatView.preview.attachButtonTitle")}
              style={{ marginRight: "4px" }}
            >
              <FiPaperclip size={18} />
            </Button>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.375rem", minWidth: 0 }}>
              {pendingDocuments.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
                  {pendingDocuments.map((doc, index) => {
                    const ft = getFileTypeInfo(doc.name);
                    return (
                      <div key={`${doc.name}-${index}`} style={{
                        display: "inline-flex", alignItems: "center", gap: "0.5rem",
                        padding: "0.375rem 0.5rem 0.375rem 0.375rem",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff", border: "1px solid #e5e7eb",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        fontSize: "0.75rem", color: "#374151", maxWidth: "260px",
                      }}>
                        <div style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: "2rem", height: "2rem", borderRadius: "6px",
                          backgroundColor: ft.bg, color: ft.fg,
                          fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.04em", flexShrink: 0,
                        }}>
                          {ft.label}
                        </div>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, fontWeight: 500 }}>
                          {doc.name}
                        </span>
                        <button
                          onClick={() => setPendingDocuments((prev) => prev.filter((_, i) => i !== index))}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: "#9ca3af", flexShrink: 0, lineHeight: 1, borderRadius: "4px", display: "flex", alignItems: "center" }}
                          title={t("chat.chatView.preview.removeDocumentTitle")}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <PreviewInput
                ref={textareaRef}
                placeholder={
                  isSubmitting
                    ? t("chat.chatView.preview.placeholderSubmitting")
                    : pendingDocuments.length > 0
                      ? t("chat.chatView.preview.placeholderDocumentInstructions")
                      : t("chat.chatView.preview.placeholderDefault")
                }
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === "Enter" && !e.shiftKey && (inputValue || pendingDocuments.length > 0)) {
                    e.preventDefault();
                    handleCreateChat();
                  }
                }}
                disabled={isSubmitting}
                rows={1}
              />
            </div>
            <Button
              onClick={() => {
                if (!inputValue && pendingDocuments.length === 0) return;
                handleCreateChat();
              }}
              disabled={(!inputValue && pendingDocuments.length === 0) || isSubmitting}
            >
              <FiSend size={18} />
            </Button>
          </PreviewInputWrapper>
          <PreviewDisclaimer>
            {t("chat.chatView.preview.disclaimer")}
          </PreviewDisclaimer>
          {!inputValue && (
            <SuggestionChipsWrapper>
              {SUGGESTIONS.map((s) => (
                <SuggestionChip key={s.label} onClick={() => handleSuggestionClick(s.prompt)}>
                  <SuggestionChipIcon $bg={s.bg} $fg={s.fg}>{s.icon}</SuggestionChipIcon>
                  {s.label}
                </SuggestionChip>
              ))}
            </SuggestionChipsWrapper>
          )}
        </PreviewContentWrapper>
      ) : (
        <Chat chat={chat} sendMessage={handleSendMessage} sendDocument={handleSendDocument} onMessageFeedback={handleMessageFeedback} streamingText={streamingText} thinkingText={thinkingText} streamingBotMessageId={streamingBotMessageId} />
      )}
    </ChatViewWrapper>
    </>
  );
}
